import React, { useState } from 'react';
import { NameEmailAvatar } from '../../../../../../ui';
import { ContextMenu, MenuItemType } from '../../../../../../ui/components/Common/ContextMenu/ContextMenu';
import { RefreshListIcons } from '../../../../../../icons/icons';
import { ContextMenuButton } from '../../../../../../ui/components/Common/ContextMenu/ContextMenuButton';
import { CandidateListType } from '../index';

const TooltipWrapper: React.FC<{ text: string }> = ({ text }) => {
  return (
      <div className='relative group w-full flex items-center justify-start text-left'>
        <div className='max-w-[180px] truncate whitespace-nowrap overflow-hidden'>
          {text || '-'}
        </div>
        <div className='absolute z-[9999] hidden group-hover:flex flex-col items-center'>
          <div className='relative'>
            <div className='absolute bottom-full px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg w-max max-w-xs whitespace-normal'>
              {text}
            </div>
          </div>
        </div>
      </div>
  );
};

const ActionMenu: React.FC<{ row: CandidateListType; onRetake: (row: CandidateListType) => void }> = ({ row, onRetake }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const menuItems: MenuItemType<CandidateListType>[] = [
    {
      label: 'Remove Invite',
      icon: <RefreshListIcons />,
      permission: [],
      onClick: () => onRetake(row),
    },
  ];

  if (!row.invited) {
    return null;
  }

  return (
      <>
        <ContextMenuButton
            row={row}
            onOpenMenu={handleOpenMenu}
            onCloseMenu={handleCloseMenu}
            anchorEl={anchorEl}
        />
        <ContextMenu
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            row={row}
            menuItems={menuItems}
        />
      </>
  );
};

// Add near the top of getColumns.tsx, above the components
const formatStatusLabel = (value: string): string => {
  if (!value) return '';

  // Split on hyphens/underscores in case the value looks like
  // "Psychometric-Inprogress"
  return value
      .split(/[-_]/)
      .map((part) =>
          part
              // Insert a space before "progress" when preceded by "In"
              .replace(/Inprogress/gi, 'In Progress')
              // Fallback: split any other run-together CamelCase word boundaries
              .replace(/([a-z])([A-Z])/g, '$1 $2')
      )
      .join(' ')
      .trim();
};

export const getColumns = (onRetake: (row: CandidateListType) => void) => [
  {
    key: 'candidate',
    label: 'Candidate',
    width: '200px',
    align: 'left' as const,
    disableSort: true,
    render: (row: CandidateListType) => (
        <NameEmailAvatar
            email={row.email}
            name={row.name}
            avatar={''}
        />
    ),
  },
  {
    key: 'applicantStatus',
    label: 'Applicant Status',
    width: '200px',
    align: 'left' as const,
    disableSort: false,
    render: (row: CandidateListType) => <TooltipWrapper text={formatStatusLabel(row.applicantStatus)} />
  },
  {
    key: 'psychometricStatus',
    label: 'Status',
    width: '200px',
    align: 'left' as const,
    disableSort: false,
    render: (row: CandidateListType) => <TooltipWrapper text={formatStatusLabel(row.psychometricStatus)} />
  },
  {
    key: 'invited',
    label: 'Invited',
    width: '100px',
    align: 'left' as const,
    disableSort: false,
    render: (row: CandidateListType) => (
        <TooltipWrapper text={row.invited ? 'YES' : 'NO'} />
    ),
  },
  {
    key: 'actions',
    label: '',
    width: '50px',
    align: 'right' as const,
    disableSort: true,
    render: (row: CandidateListType) => <ActionMenu row={row} onRetake={onRetake} />,
  },
];